import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckIcon,
  SmartToy as AgentIcon,
  Build as ToolIcon,
  QuestionAnswer as FAQIcon,
  MenuBook as GlossaryIcon,
  Lightbulb as TipIcon,
  Timeline as WorkflowIcon,
} from '@mui/icons-material';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import GuideSidebar, { SidebarSection } from './components/GuideSidebar';
import WorkflowStep from './components/WorkflowStep';
import TipCard from './components/TipCard';
import {
  whatIsAgentContent,
  whatToolDoesContent,
  workflowSteps,
  glossaryTerms,
  faqItems,
  successTips,
} from './content/guideContent';

// Sidebar sections
const sidebarSections: SidebarSection[] = [
  { id: 'what-is-agent', label: 'What is an AI Agent?' },
  { id: 'what-tool-does', label: 'What This Tool Does' },
  { id: 'workflow', label: 'The 6-Step Workflow' },
  { id: 'glossary', label: 'Glossary' },
  { id: 'tips', label: 'Tips for Success' },
  { id: 'faq', label: 'FAQ' },
];

const UserGuide: React.FC = () => {
  useDocumentTitle('User Guide');
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('what-is-agent');

  // Handle hash navigation on load
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      setActiveSection(sectionId);
      // Scroll to section after a brief delay for rendering
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  // Handle sidebar navigation
  const handleSectionClick = useCallback((sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Update URL hash without triggering a full navigation
    window.history.replaceState(null, '', `#${sectionId}`);
  }, []);

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = sidebarSections.map((s) => ({
        id: s.id,
        element: document.getElementById(s.id),
      }));

      for (const section of sections) {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom > 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Sidebar Navigation */}
        <GuideSidebar
          sections={sidebarSections}
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
        />

        {/* Main Content */}
        <Box sx={{ flex: 1, maxWidth: 900 }}>
          {/* Hero Section */}
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Welcome to Agent Architect
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
              Your guide to designing powerful AI agents - no coding required to
              get started.
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              This guide will walk you through everything you need to know to
              create your first AI agent. Whether you're a product manager,
              developer, or business analyst, you'll feel confident within
              minutes.
            </Typography>
          </Paper>

          {/* Section 1: What is an AI Agent */}
          <Box id="what-is-agent" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <AgentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                {whatIsAgentContent.title}
              </Typography>
            </Box>

            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              {whatIsAgentContent.intro}
            </Typography>

            {/* Key Points Grid */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {whatIsAgentContent.keyPoints.map((point, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      {point.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {point.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Real-world Examples */}
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Real-World Examples
            </Typography>
            <List>
              {whatIsAgentContent.examples.map((example, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={example.title}
                    secondary={example.description}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Agent Components */}
            <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
              The Building Blocks of an Agent
            </Typography>
            <Grid container spacing={2}>
              {whatIsAgentContent.components.map((component, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                  >
                    <Chip
                      label={index + 1}
                      size="small"
                      color="primary"
                      sx={{ minWidth: 28, fontWeight: 600 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {component.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {component.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: What This Tool Does */}
          <Box id="what-tool-does" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <ToolIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                {whatToolDoesContent.title}
              </Typography>
            </Box>

            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              {whatToolDoesContent.intro}
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What You'll Get
              </Typography>
              <List dense>
                {whatToolDoesContent.outcomes.map((outcome, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={outcome} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Who Is This For?
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {whatToolDoesContent.audience.map((item, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="primary.main"
                    >
                      {item.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.benefit}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Typography variant="body2" color="text.secondary">
              <strong>Time estimate:</strong> {whatToolDoesContent.timeEstimate}
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: The 6-Step Workflow */}
          <Box id="workflow" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <WorkflowIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                The 6-Step Workflow Explained
              </Typography>
            </Box>

            <Typography variant="body1" paragraph>
              Agent Architect guides you through six steps to transform your
              idea into a complete agent design. Here's what to expect at each
              step:
            </Typography>

            {workflowSteps.map((step, index) => (
              <WorkflowStep key={step.id} step={step} stepNumber={index + 1} />
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Glossary */}
          <Box id="glossary" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <GlossaryIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                Glossary of Terms
              </Typography>
            </Box>

            <Typography variant="body1" paragraph>
              Don't worry if some terms are new to you. Here's a quick reference
              guide:
            </Typography>

            <Grid container spacing={2}>
              {glossaryTerms.map((term, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="primary.main"
                    >
                      {term.term}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {term.definition}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: Tips for Success */}
          <Box id="tips" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <TipIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                Tips for Success
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {successTips.map((tip, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <TipCard title={tip.title} description={tip.description} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: FAQ */}
          <Box id="faq" sx={{ mb: 6, scrollMarginTop: 100 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <FAQIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                Frequently Asked Questions
              </Typography>
            </Box>

            {faqItems.map((item, index) => (
              <Accordion key={index} variant="outlined" sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>{item.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Call to Action */}
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'success.50',
              borderColor: 'success.200',
              border: 1,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Ready to Design Your First Agent?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You now have everything you need to get started. Remember, you can
              always come back to this guide if you need help along the way.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Design New Agent" in the navigation bar to begin your
              journey.
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default UserGuide;
